import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReviewCounts } from "../../context/ReviewCountsContext";
import { formatReviewCount } from "../../constants/reviewCounts";
import CountBadge from "../ui/CountBadge";

function BellIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z"
        fill="#914985"
      />
    </svg>
  );
}

export default function ReviewBell() {
  const navigate = useNavigate();
  const { total, pendingItems, loading, error, refresh } = useReviewCounts();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleOpen = () => {
    setOpen((prev) => !prev);
    if (!open) {
      refresh({ silent: true });
    }
  };

  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className="header__review-btn"
        onClick={handleOpen}
        aria-label={total > 0 ? `${formatReviewCount(total)} مورد نیازمند بررسی` : "موارد نیازمند بررسی"}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <BellIcon />
        <CountBadge count={total} className="header__review-badge" />
      </button>

      {open && (
        <div className="header__review-panel" role="menu">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className="text-[13px] yekanSemiBold text-[#1F2937]">موارد نیازمند بررسی</p>
              <p className="text-[11px] text-[#737373] mt-0.5">
                {total > 0
                  ? `${formatReviewCount(total)} مورد هنوز بررسی نشده است`
                  : "در حال حاضر مورد بررسی‌نشده‌ای وجود ندارد"}
              </p>
            </div>
            <button
              type="button"
              className="text-[11px] yekanSemiBold text-[#00AFA8]"
              onClick={() => refresh()}
            >
              بروزرسانی
            </button>
          </div>

          {error ? (
            <p className="text-[12px] text-red-600 bg-red-50 rounded-[8px] px-3 py-2">{error}</p>
          ) : loading && pendingItems.length === 0 && total === 0 ? (
            <p className="text-[12px] text-[#737373] py-3 text-center">در حال دریافت...</p>
          ) : pendingItems.length === 0 ? (
            <p className="text-[12px] text-[#15803D] bg-emerald-50 rounded-[8px] px-3 py-3 text-center">
              همه موارد بررسی شده‌اند
            </p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {pendingItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  onClick={() => handleNavigate(item.path)}
                  className="flex items-center justify-between gap-3 rounded-[10px] px-3 py-2.5 text-right hover:bg-gray-50 transition-colors"
                >
                  <span className="flex flex-col items-start min-w-0">
                    <span className="text-[12px] yekanSemiBold text-[#1F2937]">{item.label}</span>
                    <span className="text-[10px] text-[#737373]">{item.description}</span>
                  </span>
                  <span
                    className="shrink-0 min-w-[28px] h-[28px] rounded-full flex items-center justify-center text-[12px] yekanBold text-white"
                    style={{ background: item.accent }}
                  >
                    {formatReviewCount(item.count, { compact: true })}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
